import { API } from '@bale-bot/config';
import type {
  AdminUserResponse,
  AnalyticsSummary,
  AuthSessionResponse,
  BotConfigResponse,
  BotEvent,
  CreateUserRequest,
  HealthResponse,
  LoginRequest,
  Paginated,
  PreviewReply,
  SetupAdminRequest,
  UpdateUserRequest,
  UpdateBotConfigRequest,
} from '@bale-bot/types';
import type {
  AccountManager,
  InviteAccountManagerResponse,
} from '../types/account-manager';
import axios from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? API.prefix;

/** The bot preview lives next to the webhook, outside the `/api` prefix. */
const botBaseURL = baseURL.replace(/\/api\/?$/, '');

/** Name of the JS-readable cookie whose value is echoed for CSRF checks. */
const CSRF_COOKIE = 'bale_admin_csrf';

// `withCredentials` keeps the session cookie attached even if the panel is ever
// served from a different origin than the API.
export const http = axios.create({ baseURL, timeout: 15_000, withCredentials: true });

let handleUnauthorized: () => void = () => {};

/**
 * Wires the client to the auth store from `main.tsx`. Injected rather than
 * imported so the store and the client stay free of a circular dependency.
 */
export function configureApi(options: { onUnauthorized: () => void }): void {
  handleUnauthorized = options.onUnauthorized;
}

function readCsrfToken(): string | null {
  for (const part of document.cookie.split(';')) {
    const [name, ...value] = part.trim().split('=');
    if (name === CSRF_COOKIE) return decodeURIComponent(value.join('='));
  }
  return null;
}

http.interceptors.request.use((config) => {
  // The session travels in an HttpOnly cookie; only the CSRF echo is our job.
  const method = (config.method ?? 'get').toLowerCase();
  if (method !== 'get' && method !== 'head') {
    const csrfToken = readCsrfToken();
    if (csrfToken) config.headers['x-csrf-token'] = csrfToken;
  }
  return config;
});

http.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      // Session expired or revoked: let the router bounce to /login.
      handleUnauthorized();
    }
    return Promise.reject(error);
  },
);

/** Turns an axios failure into a message worth showing a human. */
export function describeError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    return body?.error?.message ?? error.message;
  }
  return error instanceof Error ? error.message : 'Unexpected error';
}

export const api = {
  /** Sets the HttpOnly session cookie; nothing sensitive comes back. */
  async login(payload: LoginRequest): Promise<AuthSessionResponse> {
    const { data } = await http.post<AuthSessionResponse>('/auth/login', payload);
    return data;
  },

  async logout(): Promise<void> {
    await http.post('/auth/logout');
  },

  async me(): Promise<AuthSessionResponse> {
    const { data } = await http.get<AuthSessionResponse>('/auth/me');
    return data;
  },

  /** Replaces the seeded default admin and signs the new one in. */
  async setupAdmin(payload: SetupAdminRequest): Promise<AuthSessionResponse> {
    const { data } = await http.post<AuthSessionResponse>('/auth/setup-admin', payload);
    return data;
  },

  async health(): Promise<HealthResponse> {
    const { data } = await http.get<HealthResponse>('/health');
    return data;
  },

  async getSettings(): Promise<BotConfigResponse> {
    const { data } = await http.get<BotConfigResponse>('/admin/settings');
    return data;
  },

  async updateSettings(payload: UpdateBotConfigRequest): Promise<BotConfigResponse> {
    const { data } = await http.put<BotConfigResponse>('/admin/settings', payload);
    return data;
  },

  async getAnalytics(days: number): Promise<AnalyticsSummary> {
    const { data } = await http.get<AnalyticsSummary>('/admin/analytics/summary', {
      params: { days },
    });
    return data;
  },

  async getUsers(): Promise<AdminUserResponse[]> {
    const { data } = await http.get<{ items: AdminUserResponse[] }>('/admin/users');
    return data.items;
  },

  async createUser(payload: CreateUserRequest): Promise<AdminUserResponse> {
    const { data } = await http.post<AdminUserResponse>('/admin/users', payload);
    return data;
  },

  async updateUser(id: string, payload: UpdateUserRequest): Promise<AdminUserResponse> {
    const { data } = await http.patch<AdminUserResponse>(`/admin/users/${id}`, payload);
    return data;
  },

  async deleteUser(id: string): Promise<void> {
    await http.delete(`/admin/users/${id}`);
  },

  async getRecentEvents(limit: number = API.defaultPageSize): Promise<Paginated<BotEvent>> {
    const { data } = await http.get<Paginated<BotEvent>>('/admin/events', { params: { limit } });
    return data;
  },

  /** Simulates `/start` (or any text) against the saved configuration. */
  async previewMessage(text: string): Promise<PreviewReply> {
    const { data } = await http.post<PreviewReply>(
      '/bot/preview',
      { update_id: 0, message: { message_id: 1, text } },
      { baseURL: botBaseURL },
    );
    return data;
  },

  /** Simulates a tap on an inline button in the preview conversation. */
  async previewCallback(callbackData: string): Promise<PreviewReply> {
    const { data } = await http.post<PreviewReply>(
      '/bot/preview',
      { update_id: 0, callback_query: { id: 'preview', data: callbackData, message: { message_id: 1 } } },
      { baseURL: botBaseURL },
    );
    return data;
  },

  /* ---- Account managers (invite / verify lifecycle) ---- */

  async getAccountManagers(): Promise<AccountManager[]> {
    const { data } = await http.get<{ items: AccountManager[]; total: number }>(
      '/admin/account-managers',
    );
    return data.items;
  },

  async inviteAccountManager(baleUserId: number): Promise<InviteAccountManagerResponse> {
    const { data } = await http.post<InviteAccountManagerResponse>(
      '/admin/account-managers',
      { baleUserId },
    );
    return data;
  },

  async updateAccountManager(
    id: string,
    payload: { status: 'active' | 'disabled' },
  ): Promise<AccountManager> {
    const { data } = await http.patch<AccountManager>(`/admin/account-managers/${id}`, payload);
    return data;
  },

  async deleteAccountManager(id: string): Promise<void> {
    await http.delete(`/admin/account-managers/${id}`);
  },
};